import { useEffect, useMemo, useRef } from 'react';

import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import type { AnalyticsTracker } from '@backstage/core-plugin-api';

import { createApolloClient } from '@spotify/confidence-graphql';
import { reportBackendWrite } from '@spotify/plugin-confidence-tracking-react';

import { Region } from '../../utils';

type UseConfidenceApolloClientOptions = {
  token?: string;
  uri: string;
  region?: Region;
  analyticsApi: AnalyticsTracker;
  requestAccounting: boolean;
  isLocalGraphql: boolean;
};

type ApolloClientCredentials = {
  token?: string;
  analyticsApi: AnalyticsTracker;
  requestAccounting: boolean;
};

export const useConfidenceApolloClient = ({
  token,
  uri,
  region,
  analyticsApi,
  requestAccounting,
  isLocalGraphql,
}: UseConfidenceApolloClientOptions): ApolloClient<NormalizedCacheObject> => {
  const credentialsRef = useRef<ApolloClientCredentials>({
    token,
    analyticsApi,
    requestAccounting,
  });
  credentialsRef.current = { token, analyticsApi, requestAccounting };

  const hasGraphqlAccess = Boolean(token && region && uri);

  const apolloClient = useMemo(() => {
    if (isLocalGraphql) {
      // eslint-disable-next-line no-console
      console.warn(`[Confidence] GraphQL pointing at local gateway: ${uri}`);
    }
    if (!hasGraphqlAccess) {
      return createApolloClient({});
    }
    return createApolloClient({
      token: credentialsRef.current.token,
      uri,
      region,
      getAccessToken: () => credentialsRef.current.token,
      getAnalyticsApi: () =>
        credentialsRef.current.requestAccounting
          ? credentialsRef.current.analyticsApi
          : undefined,
      onBackendWrite: reportBackendWrite,
    });
  }, [hasGraphqlAccess, uri, region, isLocalGraphql]);

  useEffect(() => {
    return () => {
      apolloClient.stop();
    };
  }, [apolloClient]);

  return apolloClient;
};
